import { useEffect, useId, useState, type FormEvent } from 'react';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { reopenDeal } from '../api';
import type { DealRecord } from '../types';
import { FieldBlock } from './bits';
import styles from './dialogs.module.css';

/**
 * Deal-Akte 02 „Wieder öffnen": an admin reopens a lost deal with a reason (`POST /deals/:id/reopen`);
 * the deal returns to the stage before the loss, the unit is re-linked when still free.
 */

export interface ReopenDialogProps {
  open: boolean;
  dealId: string;
  contactName: string;
  line: string;
  lastStageName: string;
  onClose: () => void;
  onDone: (deal: DealRecord, notice: string | null) => void;
}

export function ReopenDialog({
  open,
  dealId,
  contactName,
  line,
  lastStageName,
  onClose,
  onDone,
}: ReopenDialogProps) {
  const formId = useId();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError(null);
    setBanner(null);
  }, [open, dealId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    if (reason.trim() === '') {
      setError('Bitte geben Sie einen Grund an.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      const result = await reopenDeal(dealId, reason.trim());
      onDone(result.deal, result.notice);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.fields['reason'])
        setError(err.fields['reason']);
      else setBanner(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      tone="info"
      title="Deal wieder öffnen"
      subtitle={`${contactName} · ${line}`}
      className={styles.stageDialog}
      initialFocus="first"
      footerNote={`Der Deal startet wieder in „${lastStageName}“; die Einheit wird erneut reserviert, falls sie noch frei ist.`}
      footer={
        <>
          <Button variant="secondary" className={styles.footBtn} onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} variant="dark" className={styles.footBtn} loading={pending}>
            Wieder öffnen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <FieldBlock label="Grund" note="Pflichtangabe" error={error} htmlFor={`${formId}-reason`}>
          <textarea
            id={`${formId}-reason`}
            className={clsx(styles.input, styles.textarea, error && styles.inputInvalid)}
            rows={3}
            value={reason}
            aria-invalid={error ? true : undefined}
            onChange={(event) => setReason(event.target.value)}
          />
        </FieldBlock>
      </form>
    </Dialog>
  );
}
